import { useState } from 'react';
import { Form, message, Modal, notification, Upload } from 'antd';
import { useIntl } from 'react-intl';
import { UploadCloud } from 'react-feather';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { UploadFile } from 'antd/lib/upload/interface';

import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';
import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';

import AntdFeatherIcon from '../AntdFeatherIcon';
import { validateImage } from '@/utils/functions/validateImage';

import { compressImage } from '@/utils/functions/compressImage';
import CroperUpload from './CroperUpload';

export type { UploadFile };

function getBase64(img: Blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(img);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

interface ImagePickerFormFieldProps {
  fieldName: string;
  maxFileCount: number;
  label?: string;
  norificationSuccessHide?: boolean;
  isCrop?: boolean;
  disabled?: boolean;
  handleRemoveLogo?: () => void;
  handleUpdateLogo?: (file: File) => void;
  saveToArchive?: boolean;
}

function ImagePickerFormField({
  fieldName,
  maxFileCount,
  label,
  norificationSuccessHide = false,
  isCrop,
  disabled,
  handleRemoveLogo,
  handleUpdateLogo,
  saveToArchive = true,
}: ImagePickerFormFieldProps) {
  const intl = useIntl();

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>();

  const handleCancel = () => {
    setPreviewVisible(false);
  };

  const handleRemove = async (file: UploadFile<UploadArchiveResult>) => {
    if (handleRemoveLogo && file) {
      handleRemoveLogo();
    }
  };

  const handlePreview = async (file: UploadFile<UploadArchiveResult>) => {
    if (!file.url && !file.preview) {
      file.preview = (await getBase64(file.originFileObj as Blob)) as string; // eslint-disable-line
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const customRequest = async ({
    onSuccess,
    onError,
    file,
  }: UploadRequestOption) => {
    if (typeof file === 'string') {
      return;
    }
    try {
      const compressedFile = await compressImage(file as File);

      const isValid = (compressedFile?.size || 0) / 1024 < 200;
      if (!isValid) {
        message.error('Arquivo deve ser menor que 200KB!');
        return;
      }

      validateImage(file, isCrop);

      let response: UploadArchiveResult | undefined;

      if (saveToArchive) {
        response = await uploadArchive({
          file: [compressedFile as Blob],
          isPublic: false,
        });
      }

      if (file && handleUpdateLogo) {
        handleUpdateLogo(compressedFile);
      }

      if (onSuccess) {
        onSuccess(response);

        if (!norificationSuccessHide) {
          notification.success({
            message: intl.formatMessage({
              id: 'commons.archive.upload.message',
            }),
            description: intl.formatMessage({
              id: 'commons.archive.upload.description',
            }),
          });
        }
      }
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((err as any)?.message !== 'Image-error') {
        notification.error({
          message: intl.formatMessage({
            id: 'commons.archive.upload.error.message',
          }),
          description: intl.formatMessage({
            id: 'commons.archive.upload.error.description',
          }),
        });
      }

      if (onError) {
        onError(err as any); // eslint-disable-line
      }
    }
  };

  const normFile = (e: {
    file: UploadFile<UploadArchiveResult>;
    fileList: UploadFile<UploadArchiveResult>[];
  }) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  return (
    <>
      <Form.Item
        noStyle
        name={fieldName}
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        {isCrop ? (
          <CroperUpload
            customRequest={customRequest}
            maxFileCount={maxFileCount}
            handleRemove={handleRemove}
            handlePreview={handlePreview}
            label={label}
          />
        ) : (
          <Upload
            listType="picture-card"
            customRequest={customRequest}
            maxCount={maxFileCount}
            onPreview={handlePreview}
            disabled={disabled}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <AntdFeatherIcon icon={<UploadCloud />} />
              <div>{label || 'Upload'}</div>
            </div>
          </Upload>
        )}
      </Form.Item>

      <Modal
        title="Preview"
        open={previewVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <img alt="part" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  );
}

export default ImagePickerFormField;
